import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  HStack,
  Text,
  Box,
  Spinner,
  Button,
  Flex,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useCustomContentList } from '../../media-library/hooks/useCustomContent';

interface ClipPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onSelect: (clip: { id: string; name: string }) => void;
}

/** Single-select picker over the org's custom audio clips (completed only). */
export function ClipPickerModal({ isOpen, onClose, title, onSelect }: ClipPickerModalProps) {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    if (isOpen) {
      setSearchInput('');
      setSearch('');
    }
  }, [isOpen]);

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useCustomContentList({
    q: search || undefined,
  });
  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <InputGroup mb={3}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder={t('player.announcement.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </InputGroup>

          {isLoading ? (
            <Flex justify="center" py={10}>
              <Spinner color="brand.500" />
            </Flex>
          ) : items.length === 0 ? (
            <Text color="gray.400" py={8} textAlign="center">
              {t('player.announcement.empty')}
            </Text>
          ) : (
            <VStack align="stretch" spacing={0}>
              {items.map((clip) => {
                const ready = clip.processingStatus === 'completed';
                return (
                  <HStack
                    key={clip.id}
                    py={2.5}
                    px={2}
                    spacing={3}
                    _hover={{ bg: ready ? 'gray.50' : undefined }}
                    borderRadius="md"
                    cursor={ready ? 'pointer' : 'not-allowed'}
                    opacity={ready ? 1 : 0.5}
                    onClick={() => {
                      if (!ready) return;
                      onSelect({ id: clip.id, name: clip.title });
                      onClose();
                    }}
                  >
                    <Box flex={1} minW={0}>
                      <Text fontSize="sm" color="gray.800" noOfLines={1}>
                        {clip.title}
                      </Text>
                      {!ready && (
                        <Text fontSize="xs" color="gray.400">
                          {t('player.announcement.processing')}
                        </Text>
                      )}
                    </Box>
                  </HStack>
                );
              })}
              {hasNextPage && (
                <Flex justify="center" mt={3}>
                  <Button size="sm" variant="ghost" onClick={() => fetchNextPage()} isLoading={isFetchingNextPage}>
                    {t('playlists.loadMore')}
                  </Button>
                </Flex>
              )}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default ClipPickerModal;
